import { apiClient } from './client';

export interface Speaker {
  id: string;
  nome: string;
  cognome: string;
  bio?: string;
  photo_url?: string;
  azienda?: string;
  linkedin_url?: string;
  email?: string;
  role: string;
  sort_order: number;
}

export interface CourseMaterial {
  id: string;
  edition_id: string;
  title: string;
  description?: string;
  type: 'pdf' | 'ppt' | 'pptx' | 'video' | 'link' | 'recording';
  file_path?: string;
  external_url?: string;
  file_size_bytes?: number;
  available_from: 'registration' | 'attended';
  accessible: boolean;
  sort_order: number;
  created_at: string;
}

export interface EditionMaterials {
  editionId: string;
  editionLabel: string;
  editionScheduledAt: string;
  materials: CourseMaterial[];
}

export interface EditionContent {
  speakers: Speaker[];
  materialsByEdition: EditionMaterials[];
  enrollmentStatus: string;
}

export async function getMyContent(token: string): Promise<EditionContent> {
  const res = await apiClient.get('/enrollment/me/content', {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data;
}

export async function downloadMaterial(materialId: string, title: string): Promise<void> {
  const res = await apiClient.get(`/materials/${materialId}/download`, {
    responseType: 'blob',
  });
  const url = URL.createObjectURL(res.data);
  const a = document.createElement('a');
  a.href = url;
  a.download = title;
  a.click();
  URL.revokeObjectURL(url);
}

// Admin
export async function getAdminEditionContent(editionId: string) {
  const res = await apiClient.get(`/admin/editions/${editionId}/content`);
  return res.data;
}

export async function getAdminSpeakers() {
  const res = await apiClient.get('/admin/speakers');
  return res.data.speakers as Speaker[];
}

export async function createSpeaker(formData: FormData) {
  const res = await apiClient.post('/admin/speakers', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return res.data.speaker;
}

export async function updateSpeaker(id: string, formData: FormData) {
  const res = await apiClient.put(`/admin/speakers/${id}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return res.data.speaker;
}

export async function deleteSpeaker(id: string) {
  await apiClient.delete(`/admin/speakers/${id}`);
}

export async function assignSpeakerToEdition(editionId: string, speakerId: string, role: string) {
  await apiClient.post(`/admin/editions/${editionId}/speakers`, { speaker_id: speakerId, role });
}

export async function removeSpeakerFromEdition(editionId: string, speakerId: string) {
  await apiClient.delete(`/admin/editions/${editionId}/speakers/${speakerId}`);
}

export async function sendSpeakerLink(editionId: string, speakerId: string) {
  await apiClient.post(`/admin/editions/${editionId}/speakers/${speakerId}/send-link`);
}

export async function createMaterial(formData: FormData) {
  const res = await apiClient.post('/admin/materials', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return res.data.material;
}

export async function updateMaterial(id: string, data: Partial<CourseMaterial>) {
  const res = await apiClient.put(`/admin/materials/${id}`, data);
  return res.data.material;
}

export async function deleteMaterial(id: string) {
  await apiClient.delete(`/admin/materials/${id}`);
}

export async function adminDownloadMaterial(id: string, title: string, ext: string): Promise<void> {
  const res = await apiClient.get(`/admin/materials/${id}/download`, { responseType: 'blob' });
  const url = URL.createObjectURL(res.data);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${title}${ext}`;
  a.click();
  URL.revokeObjectURL(url);
}
